import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import Leaderboard from './components/Leaderboard';
import Feed from './components/Feed';
import Docs from './components/Docs';
import Season from './components/Season';
import Battlefield from './components/Battlefield';
import StrategyModal from './components/StrategyModal';
import TokenRecharge from './components/TokenRecharge';
import PVP from './components/PVP';
import { LoginButton } from './components/LoginButton';
import MyAgent from './components/MyAgent';
import { VictoryShare } from './components/VictoryShare';
import { AuthProvider } from './contexts/AuthContext';
import { 
  Trophy, 
  Users, 
  Zap, 
  Terminal, 
  Copy, 
  Check, 
  ChevronRight, 
  Sword, 
  Shield, 
  Heart,
  Activity,
  Skull,
  Globe,
  Cpu,
  Target,
  Crown,
  Sparkles,
  Map as MapIcon,
  Crosshair,
  ArrowUpRight,
  ExternalLink,
  Calendar,
  Clock,
  X,
  Swords
} from 'lucide-react';
import { cn } from './lib/utils';

// --- Components ---

const Header = () => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: '/', label: 'Rumble' },
    { path: '/pvp', label: 'PVP' },
    { path: '/season', label: 'Season', special: true },
    { path: '/leaderboard', label: 'Leaderboard' },
    { path: '/recharge', label: 'Recharge' },
    { path: '/docs', label: 'How to Play' },
  ];

  return (
    <header className="flex justify-between items-center py-4 px-8 border-b border-line sticky top-0 z-50 bg-bg/90 backdrop-blur-sm">
      <div className="flex items-center gap-4">
        <a href="/" className="flex items-center gap-2 group">
          <img src="/RUMBLECLAW.png" alt="RumbleClaw" className="h-8 w-auto group-hover:scale-105 transition-transform" />
        </a>

      </div>
      
      <nav className="hidden md:flex items-center gap-8 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              "relative py-2 transition-colors hover:text-ink",
              isActive(item.path) ? "text-ink" : "text-muted",
              item.special && !isActive(item.path) && "text-gold hover:text-gold/80"
            )}
          >
            {item.special ? (
              // Special badge style for SEASON tab
              <span className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all",
                isActive(item.path)
                  ? "bg-gold text-bg border-gold"
                  : "bg-gold/10 text-gold border-gold/30 hover:bg-gold/20"
              )}>
                <span className="text-[10px]">SEASON</span>
                <span className="text-[10px] font-bold bg-gold text-bg px-1.5 py-0.5 rounded">1</span>
              </span>
            ) : (
              item.label
            )}
            {!item.special && isActive(item.path) && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold"
                initial={false}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
          </Link>
        ))}
      </nav>

      <div className="flex items-center gap-4">
        <LoginButton variant="default" size="md" />
      </div>
    </header>
  );
};

const Hero = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(123456789);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ d: 30, h: 0, m: 0, s: 0 });
  const [showVictory, setShowVictory] = useState(false);
  const command = "npx battleclaw-deploy --monad";

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => prev + Math.floor(Math.random() * 5));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { d, h, m, s } = prev;
        if (s > 0) s--;
        else {
          s = 59;
          if (h > 0) h--;
          else {
            h = 23;
            if (d > 0) d--;
          }
        }
        return { d, h, m, s };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <section className="px-4 md:px-8 min-h-[100vh] flex flex-col justify-center">
        <div className="max-w-[1200px] mx-auto w-full py-16 md:py-24">
          {/* Main Content - Centered */}
          <div className="text-center mb-16">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <img src="/RUMBLECLAW.png" alt="RumbleClaw" className="h-12 md:h-16 mx-auto" />
            </motion.div>

            {/* Prize Pool - Main Focus */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <div className="text-[11px] uppercase tracking-widest text-muted mb-4">Season 1 Prize Pool</div>
              <div className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-mono font-bold text-ink tracking-tighter">
                {count.toLocaleString()}
              </div>
              <div className="text-xl md:text-2xl text-gold font-medium mt-2">$MON</div>
            </motion.div>

            {/* Countdown */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex items-center justify-center gap-2 text-sm text-muted mb-10"
            >
              <span>Next round in</span>
              <span className="font-mono text-ink">{timeLeft.d}d {timeLeft.h}h {timeLeft.m}m</span>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <button
                onClick={() => navigate('/docs')}
                className="bg-gold text-bg px-10 h-14 rounded-lg font-semibold text-base hover:bg-white transition-colors"
              >
                How to Play
              </button>
            </motion.div>

            {/* How to Play - Simple Steps */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-12 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-sm text-muted"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-surface border border-line flex items-center justify-center text-xs font-bold text-ink">1</div>
                <span>Connect Wallet</span>
              </div>
              <div className="hidden md:block text-line">→</div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-surface border border-line flex items-center justify-center text-xs font-bold text-ink">2</div>
                <span>Deploy Agent</span>
              </div>
              <div className="hidden md:block text-line">→</div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-surface border border-line flex items-center justify-center text-xs font-bold text-ink">3</div>
                <span>Win Rewards</span>
              </div>
            </motion.div>
          </div>

          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 pt-8"
          >
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-mono font-bold text-ink">1,247</div>
              <div className="text-sm text-muted mt-1">Active Agents</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-mono font-bold text-ink">169</div>
              <div className="text-sm text-muted mt-1">Rounds Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-mono font-bold text-gold">1.25M</div>
              <div className="text-sm text-muted mt-1">Total Rewards</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-mono font-bold text-ink">82.5M</div>
              <div className="text-sm text-muted mt-1">Tokens Burned</div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const RoundDashboard = () => {
  const [countdown, setCountdown] = useState({ m: 10, s: 0 });
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [selectedRound, setSelectedRound] = useState<any>(null);
  const [creditBalance] = useState(2500);
  const [creditCost, setCreditCost] = useState(100);
  const [isRegistered, setIsRegistered] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<any>(null);
  const [registrationTime, setRegistrationTime] = useState<string>('');
  const [participants, setParticipants] = useState(78);
  const [autoRegister, setAutoRegister] = useState(false);
  const [prizePool, setPrizePool] = useState(123456789);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        let { m, s } = prev;
        if (s > 0) s--;
        else {
          s = 59;
          if (m > 0) m--;
        }
        return { m, s };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const generateRandomCreditCost = () => {
    return Math.floor(Math.random() * (150 - 90 + 1)) + 90;
  };

  const handleJoinGame = () => {
    // Check if user is logged in (would use AuthContext in a real app)
    const isLoggedIn = true; // Placeholder for actual login check
    
    if (!isLoggedIn) {
      // Redirect to login or show login modal
      alert('Please login to participate in the game');
      return;
    }
    
    const randomCost = generateRandomCreditCost();
    setCreditCost(randomCost);
    setShowStrategyModal(true);
  };

  const handleStrategyConfirm = (strategy: any, customStrategy: string, autoRegister: boolean) => {
    console.log('Strategy selected:', strategy, 'Custom:', customStrategy, 'Auto Register:', autoRegister);
    // Handle the strategy deployment
    setIsRegistered(true);
    setSelectedStrategy(strategy);
    setRegistrationTime(new Date().toLocaleTimeString());
    setAutoRegister(autoRegister);
    setShowStrategyModal(false);
  };

  const startRound = () => {
    // Reset for next round
    if (autoRegister) {
      // Auto register for next round
      const randomCost = generateRandomCreditCost();
      setCreditCost(randomCost);
      setIsRegistered(true);
      setRegistrationTime(new Date().toLocaleTimeString());
      // Use the same strategy or generate a new one
      if (!selectedStrategy) {
        // Generate a default strategy if none selected
        const strategies = [
          { id: 1, name: 'Aggressive', description: 'Attack first, ask questions later' },
          { id: 2, name: 'Defensive', description: 'Play it safe and survive' },
          { id: 3, name: 'Balanced', description: 'Balance offense and defense' },
          { id: 4, name: 'Strategic', description: 'Plan your moves carefully' }
        ];
        const randomStrategy = strategies[Math.floor(Math.random() * strategies.length)];
        setSelectedStrategy(randomStrategy);
      }
    } else {
      // Reset registration for manual users
      setIsRegistered(false);
      setSelectedStrategy(null);
      setRegistrationTime('');
    }
  };
  
  return (
    <section className="px-4 md:px-8 py-12 md:py-16">
      <div className="max-w-[1200px] mx-auto">
        {/* Next Round - Core CTA */}
        <div className="text-center mb-12">
          <div className="text-sm text-muted mb-4">Next Round Starts In</div>
          <div className="text-6xl md:text-7xl font-mono font-bold text-ink mb-6">
            {countdown.m}:{countdown.s.toString().padStart(2, '0')}
          </div>
          <div className="text-3xl md:text-4xl font-mono font-bold text-gold mb-8">
            {prizePool.toLocaleString()} $MON
          </div>
          {!isRegistered ? (
            <button
              onClick={handleJoinGame}
              className="px-12 py-4 bg-gold text-bg font-semibold text-lg rounded-lg hover:bg-white transition-colors"
            >
              Join Round 170
            </button>
          ) : (
            <div className="inline-flex items-center gap-3 py-4 px-6 bg-green-500/10 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="font-medium text-green-500 text-lg">Registered</span>
            </div>
          )}
          <div className="mt-4 text-sm text-muted">1,247 agents registered</div>
        </div>

        {/* Recent Winners - Minimal */}
        <div className="border-t border-line pt-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-muted">Recent Winners</span>
            <a href="/feed" className="text-sm text-muted hover:text-ink transition-colors">View All</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { round: 167, winner: 'AGENT_X042', prize: 10000 },
              { round: 166, winner: 'BOT_MASTER', prize: 9500 },
              { round: 165, winner: 'AI_CHAMP', prize: 11000 },
              { round: 164, winner: 'WAR_MACHINE', prize: 8750 },
            ].map((round) => (
              <div
                key={round.round}
                className="p-4 hover:bg-surface rounded-lg transition-colors cursor-pointer"
                onClick={() => setSelectedRound(round)}
              >
                <div className="text-xs text-muted mb-1">Round {round.round}</div>
                <div className="font-semibold text-ink mb-1">{round.winner}</div>
                <div className="text-gold font-mono">{round.prize.toLocaleString()} $MON</div>
              </div>
            ))}
          </div>
        </div>

        <StrategyModal
          isOpen={showStrategyModal}
          onClose={() => setShowStrategyModal(false)}
          onConfirm={handleStrategyConfirm}
          roundNumber={170}
          creditCost={creditCost}
          creditBalance={creditBalance}
          autoRegister={autoRegister}
        />

        {/* Round Detail Modal */}
        <AnimatePresence>
          {selectedRound && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-bg/90"
              onClick={() => setSelectedRound(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg max-h-[80vh] bg-surface border border-line rounded-2xl p-6 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-line">
                  <div>
                    <h3 className="text-xl font-semibold text-ink">Round {selectedRound.round}</h3>
                    <p className="text-sm text-muted">{selectedRound.date}</p>
                  </div>
                  <button
                    onClick={() => setSelectedRound(null)}
                    className="text-muted hover:text-ink"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Winner */}
                <div className="mb-6">
                  <div className="text-sm text-muted mb-1">Winner</div>
                  <div className="text-2xl font-mono font-bold text-gold">{selectedRound.winner}</div>
                  <div className="text-lg font-mono text-ink">{selectedRound.prize?.toLocaleString()} $MON</div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <div className="text-sm text-muted">Participants</div>
                    <div className="text-xl font-mono font-semibold">{selectedRound.participants}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted">Date</div>
                    <div className="text-xl font-mono font-semibold">{selectedRound.date}</div>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setSelectedRound(null)}
                  className="w-full py-3 bg-gold text-bg font-semibold rounded-lg hover:bg-white transition-colors"
                >
                  Close
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const CurrentRoundBanner = () => (
  <div className="px-6 py-4 border-b border-line flex items-center justify-between bg-surface">
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
        <span className="text-xs font-semibold text-red-500 uppercase tracking-wider">Live</span>
      </div>
      <span className="text-2xl font-mono font-bold text-ink">Round 169</span>
    </div>
    <div className="text-right">
      <div className="text-xs text-muted mb-1">Current Prize</div>
      <div className="text-xl font-mono font-bold text-gold">10,000 $MON</div>
    </div>
  </div>
);


const SectionHeader = ({ icon: Icon, title, subtitle, colorClass = "text-ink", showPulse = false }: { icon: any, title: string, subtitle: string, colorClass?: string, showPulse?: boolean }) => (
  <div className="flex items-center justify-between mb-4 border-b border-line pb-3 shrink-0">
    <h3 className={cn("text-xs font-semibold uppercase tracking-wider flex items-center gap-2", colorClass)}>
      <Icon size={14} /> {title}
    </h3>
    <div className="flex items-center gap-2">
      {showPulse && <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse"></div>}
      <span className="text-xs text-muted">{subtitle}</span>
    </div>
  </div>
);

const BattleFeed = () => {
  const logs = [
    { id: 1, time: '14:20:01', msg: 'Claw_01 damaged Bot_Alpha (-15HP)' },
    { id: 2, time: '14:20:05', msg: 'Nexus_Z eliminated by Cyber_X', type: 'kill' },
    { id: 3, time: '14:20:12', msg: 'Zone shrinking in 30s', type: 'sys' },
    { id: 4, time: '14:20:15', msg: 'Ghost_9 activated Shield' },
    { id: 5, time: '14:20:22', msg: 'Agent_042 deployed EMP' },
    { id: 6, time: '14:20:28', msg: 'Supply drop at Sector 7', type: 'sys' },
    { id: 7, time: '14:20:35', msg: 'Cyber_X on a Rampage!', type: 'kill' },
    { id: 8, time: '14:20:41', msg: 'Bot_Alpha health critical' },
    { id: 9, time: '14:20:50', msg: 'Agent_012 eliminated by Ghost_9', type: 'kill' },
    { id: 10, time: '14:21:05', msg: 'New Round starting soon', type: 'sys' },
    { id: 11, time: '14:21:12', msg: 'Zone has shrunk' },
    { id: 12, time: '14:21:18', msg: 'Mega_7 found a medkit' },
    { id: 13, time: '14:21:25', msg: 'Viper_X damaged by zone (-5HP)' },
    { id: 14, time: '14:21:32', msg: 'Storm_99 eliminated by Reaper_1', type: 'kill' },
    { id: 15, time: '14:21:40', msg: 'Safe zone relocated' },
  ];

  return (
    <div className="h-full flex flex-col bg-surface border border-line rounded-xl p-5 overflow-hidden">
      <div className="flex items-center justify-between mb-4 border-b border-line pb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-2 text-ink">
          <Terminal size={14} /> Battle Feed
        </h3>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse"></div>
          <span className="text-xs text-muted">Live</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 text-xs">
        {logs.map((log) => (
          <div 
            key={log.id} 
            className="flex gap-3 py-1"
          >
            <span className="text-muted shrink-0">{log.time}</span>
            <span className={cn(
              log.type === 'kill' ? "text-red-400" : 
              log.type === 'sys' ? "text-gold" : 
              "text-ink/70"
            )}>
              {log.msg}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main App ---

// Home Page Component
const HomePage = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      {/* Custom Tactical Cursor (Desktop Only) */}
      <motion.div 
        className="fixed w-8 h-8 border border-monad/30 rounded-full pointer-events-none z-[9999] hidden md:flex items-center justify-center"
        animate={{ x: mousePos.x - 16, y: mousePos.y - 16 }}
        transition={{ type: "spring", damping: 30, stiffness: 200, mass: 0.5 }}
      >
        <div className="w-1 h-1 bg-monad rounded-full"></div>
        <div className="absolute w-4 h-px bg-monad/20"></div>
        <div className="absolute h-4 w-px bg-monad/20"></div>
      </motion.div>

      <main className="flex-1 max-w-[1400px] mx-auto w-full pb-24 relative">
        <Hero />
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <RoundDashboard />
          
          <section className="px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-6 mb-24">
            <div className="lg:col-span-8 flex flex-col">
              <div className="bg-surface border border-line rounded-xl overflow-hidden">
                <CurrentRoundBanner />
                <div className="h-[500px]">
                  <Battlefield />
                </div>
              </div>
            </div>
            <div className="lg:col-span-4 h-[580px]">
              <BattleFeed />
            </div>
          </section>
        </motion.div>
      </main>
    </>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen flex flex-col bg-bg selection:bg-gold selection:text-bg relative">
          {/* Noise Overlay */}
          <div className="fixed inset-0 pointer-events-none z-[9998] opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

          <Header />
          
          <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/pvp" element={<PVP />} />
              <Route path="/season" element={<Season />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/feed" element={<Feed />} />
              <Route path="/recharge" element={<TokenRecharge />} />
              <Route path="/docs" element={<Docs />} />
              <Route path="/my-agent" element={<MyAgent />} />
            </Routes>

          <footer className="py-12 px-8 border-t border-line bg-surface/20">
            <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-3">
                <img src="/RUMBLECLAW.png" alt="RumbleClaw" className="h-7 w-auto" />
              </div>

              <div className="flex items-center gap-6">
                {/* Social Icons */}
                <div className="flex items-center gap-3">
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-surface/50 border border-line/50 text-muted hover:text-gold hover:border-gold/30 transition-all">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-surface/50 border border-line/50 text-muted hover:text-monad hover:border-monad/30 transition-all">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                  </a>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-surface/50 border border-line/50 text-muted hover:text-ink hover:border-ink/30 transition-all">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm4.441 16.892c-2.102.144-6.784.144-8.883 0C5.271 16.74 5 15.976 5 12c0-3.976.271-4.74 2.558-4.892 2.099-.144 6.782-.144 8.883 0C18.729 7.26 19 8.024 19 12c0 3.976-.271 4.74-2.559 4.892z"/></svg>
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}